import type { TooltipProps as MuiTooltipProps } from "@mui/material";
import { Tooltip as UnstyledTooltip, styled } from "@mui/material";

/** `title` not required if error is populated */
type TitleOrError =
  | {
      error?: MuiTooltipProps["title"];
      title: MuiTooltipProps["title"];
    }
  | {
      error: MuiTooltipProps["title"];
      title?: MuiTooltipProps["title"];
    };

export type TooltipProps = Omit<MuiTooltipProps, "title"> & {
  errorPlacement?: MuiTooltipProps["placement"];
  fullWidth?: boolean;
} & TitleOrError;

// In order to pass custom styles to the tooltip slot, we create a pass-through
// Component that captures the incoming className and sends it to the tooltip
function CustomTooltip({ className, ...props }: MuiTooltipProps) {
  return <UnstyledTooltip {...props} classes={{ tooltip: className }} />;
}

const StyledTooltip = styled(CustomTooltip, {
  shouldForwardProp: (propName: keyof TooltipProps) =>
    propName !== "error" && propName !== "fullWidth",
})<TooltipProps>(({ fullWidth, theme }) =>
  fullWidth
    ? {
        maxWidth: `calc(100vw - ${theme.spacing(10)})`,

        [`@media (min-width: ${theme.spacing(192)})`]: {
          maxWidth: `calc(100vw - ${theme.spacing(20)})`,
        },
        [`@media (min-width: ${theme.spacing(240)})`]: {
          maxWidth: `calc(100vw - ${theme.spacing(30)})`,
        },
        [`@media (min-width: ${theme.spacing(320)})`]: {
          maxWidth: `calc(100vw - ${theme.spacing(50)})`,
        },
      }
    : null,
);

export const Tooltip = ({
  arrow,
  color,
  error,
  errorPlacement,
  placement,
  title,
  ...rest
}: TooltipProps) => (
  <StyledTooltip
    {...rest}
    arrow={error ? false : arrow}
    color={error ? "error" : color}
    placement={error ? (errorPlacement ?? "bottom-start") : placement}
    title={error ?? title}
  />
);
